/* Karışık klasör ayrıştırma.

   Stüdyo bir çekim klasörünü olduğu gibi sürükleyip bırakıyor: içinde hem
   JPEG dışa aktarımları hem de ham makine dosyaları oluyor. Ham dosyalar
   stüdyonun kendi arşivinde kalacak (karar: plan §5 eki), yani buraya
   yüklenmeyecekler.

   Bu yüzden ilk ham dosyada yığın durmamalı. Ayrıştırıcı kabul edilenleri
   işler, atlananları gruplar ve aradaki önemli farkı ayırır:

     - JPEG karşılığı OLAN ham dosya  → beklenen durum, sessizce atlanır
     - JPEG karşılığı OLMAYAN ham dosya → gerçek uyarı: o kare hiç
       yüklenmeyecek ve kimse fark etmeyebilir

   İkincisi sessiz veri kaybıdır; yönetici bunu görmeli. */

import { inspect, concurrencyFor, type RejectionCode } from './intake.ts';

export type FileLike = { name: string; size: number; type: string };

export type SkippedFile = {
  file: FileLike;
  code: RejectionCode;
  message: string;
  /** Aynı adı taşıyan, kabul edilmiş bir JPEG var mı? */
  hasSibling: boolean;
};

export type BatchPlan<T extends FileLike = FileLike> = {
  accepted: T[];
  skipped: SkippedFile[];
  /** JPEG karşılığı olmayan ham dosyalar — bunlar gerçekten kaybolur. */
  orphanedRaw: FileLike[];
  /** Dosya boyutuna göre ayarlanmış eşzamanlılık. */
  concurrency: number;
  totalBytes: number;
  acceptedBytes: number;
};

/** "IMG_0042.CR3" → "img_0042" — uzantısız, karşılaştırma için normalleştirilmiş. */
export function baseName(name: string): string {
  const dot = name.lastIndexOf('.');
  return (dot > 0 ? name.slice(0, dot) : name).toLowerCase().trim();
}

/**
 * Bırakılan dosyaları işlenebilir ve atlanacak olarak ayırır.
 * Hiçbir zaman hata fırlatmaz — karışık klasör normal durumdur.
 */
export function planBatch<T extends FileLike>(files: readonly T[]): BatchPlan<T> {
  const accepted: T[] = [];
  const skipped: SkippedFile[] = [];
  let totalBytes = 0;
  let acceptedBytes = 0;

  for (const file of files) {
    totalBytes += file.size;
    const result = inspect(file);
    if (result.ok) {
      accepted.push(file);
      acceptedBytes += file.size;
    } else {
      skipped.push({ file, code: result.code, message: result.message, hasSibling: false });
    }
  }

  // Kabul edilenlerin adlarına bakarak ham dosyanın karşılığı var mı bul.
  const acceptedNames = new Set(accepted.map((f) => baseName(f.name)));
  for (const s of skipped) {
    s.hasSibling = acceptedNames.has(baseName(s.file.name));
  }

  const orphanedRaw = skipped
    .filter((s) => (s.code === 'raw' || s.code === 'unsupported') && !s.hasSibling)
    .map((s) => s.file);

  const average = accepted.length > 0 ? acceptedBytes / accepted.length : 0;

  return {
    accepted,
    skipped,
    orphanedRaw,
    concurrency: concurrencyFor(average),
    totalBytes,
    acceptedBytes
  };
}

/** Yöneticiye gösterilecek özet — ne olacağını tek bakışta söyler. */
export function summarize(plan: BatchPlan): { line: string; warning: string | null } {
  const n = plan.accepted.length;
  const mb = (b: number) => (b / 1048576).toFixed(0);

  const line =
    n === 0
      ? 'Yüklenecek kare yok.'
      : `${n} kare yüklenecek (${mb(plan.acceptedBytes)} MB).`;

  const paired = plan.skipped.filter((s) => s.hasSibling).length;
  const parts: string[] = [];

  if (paired > 0) {
    parts.push(`${paired} ham dosya atlandı — JPEG karşılıkları yüklendi, arşivinizde kalacaklar.`);
  }

  if (plan.orphanedRaw.length > 0) {
    const names = plan.orphanedRaw.slice(0, 3).map((f) => f.name).join(', ');
    const more = plan.orphanedRaw.length > 3 ? ` ve ${plan.orphanedRaw.length - 3} tane daha` : '';
    parts.push(
      `DİKKAT: ${plan.orphanedRaw.length} ham dosyanın JPEG karşılığı yok (${names}${more}). ` +
        `Bu kareler hiç yüklenmeyecek — JPEG olarak dışa aktarıp tekrar bırakın.`
    );
  }

  const others = plan.skipped.filter(
    (s) => !s.hasSibling && s.code !== 'raw' && s.code !== 'unsupported'
  );
  for (const o of others) parts.push(o.message);

  return { line, warning: parts.length > 0 ? parts.join(' ') : null };
}
